/**
 * Mitmekeelne väli + sisukeele-valija (aishopi muster) - ISE-SISALDAV variant
 * `@wa/ui/plans`-i jaoks: ei sõltu web_admin'i `i18n/config`-ist ega framer'ist.
 * `LangTabs` valib aktiivse sisu-keele kompaktse comboboxiga; `MultilingualField`
 * seob ühe välja `value[activeLocale]`-iga + luku-lüliti, mis lukus olles
 * peegeldab sisestuse KÕIKIDESSE keeltesse.
 */
import { useState } from "react";
import { Check, ChevronDown, Lock, Unlock } from "lucide-react";
import { cn } from "../lib/utils";
import { Input } from "../components/input";
import { Textarea } from "../components/textarea";
import { Label } from "../components/label";
import { Popover, PopoverContent, PopoverTrigger } from "../components/popover";
import { LocaleFlag } from "../components/locale-flag";
import { makeT, type T } from "./i18n";
import type { Locale, LocaleMap } from "./types";

/** Keele endonüümid valiku-listile; tundmatu keel → kood suurtähtedega. */
const LOCALE_LABEL: Record<string, string> = {
  et: "Eesti", en: "English", ru: "Русский", lv: "Latviešu", lt: "Lietuvių",
  fi: "Suomi", sv: "Svenska", de: "Deutsch", uk: "Українська", fr: "Français", es: "Español",
};

const localeLabel = (loc: string) => LOCALE_LABEL[loc] ?? loc.toUpperCase();

/** Tuleta per-lokaal „sisu olemas" kaart väljade komplektist. */
export function deriveFilled(locales: readonly string[], ...fields: Array<LocaleMap | undefined>): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const loc of locales) out[loc] = fields.some((f) => Boolean(f?.[loc]?.trim()));
  return out;
}

/**
 * Sisukeele-valija - kompaktne combobox (lipp + kood + täituvus-punkt + chevron).
 * Varem horisontaalne tab-riba; nüüd rippmenüü Radix Popoveriga, mis renderdab
 * portaali → ei lõigata dialoogi kerimis-konteineri `overflow`-iga. Kõik kaardi
 * mitmekeelsed väljad seonduvad endiselt `value[active]`-iga.
 */
export function LangTabs({
  active, onChange, locales, filled, uiLocale,
}: {
  active: string; onChange: (l: string) => void; locales: readonly string[];
  filled?: Record<string, boolean>; uiLocale: Locale;
}) {
  const t = makeT(uiLocale);
  const [open, setOpen] = useState(false);
  if (locales.length < 2) return null;
  const activeFilled = filled?.[active] === true;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={localeLabel(active)}
          className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 text-xs font-semibold text-foreground transition-colors hover:bg-muted"
        >
          <LocaleFlag locale={active} />
          <span className="uppercase tracking-wider">{active}</span>
          {filled && (
            <span
              className={cn("size-1.5 rounded-full", activeFilled ? "bg-success" : "bg-muted-foreground/30")}
              title={activeFilled ? t("ml.added") : t("ml.missing")}
            />
          )}
          <ChevronDown className={cn("size-3 opacity-60 transition-transform", open && "rotate-180")} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-48 p-1.5">
        {locales.map((loc) => {
          const isActive = loc === active;
          const has = filled?.[loc] === true;
          return (
            <button
              key={loc}
              type="button"
              onClick={() => { onChange(loc); setOpen(false); }}
              className={cn(
                "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors",
                isActive ? "bg-accent font-semibold text-primary" : "text-foreground hover:bg-accent/50",
              )}
            >
              <LocaleFlag locale={loc} />
              <span className="flex-1">{localeLabel(loc)}</span>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{loc}</span>
              {filled && (
                <span
                  className={cn("size-1.5 rounded-full", has ? "bg-success" : "bg-muted-foreground/30")}
                  title={has ? t("ml.added") : t("ml.missing")}
                />
              )}
              {isActive && <Check className="size-3.5 text-primary" />}
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

export function MultilingualField({
  label, required, value, onChange, activeLocale, locales, as = "input", rows = 2,
  placeholder, uiLocale,
}: {
  label: string; required?: boolean; value: LocaleMap; onChange: (next: LocaleMap) => void;
  activeLocale: string; locales: readonly string[]; as?: "input" | "textarea"; rows?: number;
  placeholder?: string; uiLocale: Locale;
}) {
  const t: T = makeT(uiLocale);
  const [linked, setLinked] = useState(true);
  const current = value[activeLocale] ?? "";
  const multi = locales.length > 1;

  function spread(next: string): LocaleMap {
    return { ...value, ...Object.fromEntries(locales.map((l) => [l, next])) };
  }
  function handleChange(next: string) {
    onChange(linked && multi ? spread(next) : { ...value, [activeLocale]: next });
  }
  function toggleLock() {
    if (!linked) onChange(spread(current));
    setLinked((l) => !l);
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs">
          {label} {required && <span className="text-primary">*</span>}
          {multi && <span className="ml-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">{activeLocale}</span>}
        </Label>
        {multi && (
          <button
            type="button"
            onClick={toggleLock}
            title={linked ? t("ml.linked") : t("ml.unlinked")}
            className={cn(
              "inline-flex size-6 items-center justify-center rounded-md transition-colors",
              linked ? "text-primary hover:bg-primary/10" : "text-muted-foreground hover:bg-accent hover:text-foreground",
            )}
          >
            {linked ? <Lock className="size-3.5" /> : <Unlock className="size-3.5" />}
          </button>
        )}
      </div>
      {as === "textarea" ? (
        <Textarea key={`${activeLocale}-${linked ? "L" : "U"}`} rows={rows} value={current} onChange={(e) => handleChange(e.target.value)} placeholder={placeholder} />
      ) : (
        <Input key={`${activeLocale}-${linked ? "L" : "U"}`} value={current} onChange={(e) => handleChange(e.target.value)} placeholder={placeholder} />
      )}
    </div>
  );
}
